/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { Box, styled } from "@mui/material";
import _ from "lodash";
import React from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import ContactCard from "../components/ContactCard";
import { PageContainer } from "../components/layout/PageContainer";
import Section from "../components/layout/Section";
import { Loader } from "../components/Loader";
import TitleBox from "../components/TitleBox";
import {
  selectContactInfo,
  selectCurrentFarmerAccount,
  selectName,
} from "../features/azure-b2c/storage";
import { QuickLinks } from "../features/dashboard/components/QuickLinks";
import SearchBar from "../features/dashboard/components/SearchBar";
import { TitleWithLink } from "../features/dashboard/components/TitleWithLink";
import OrderCard from "../features/orders/components/OrderCard";
import { NoOrders } from "../features/orders/NoOrders";
import {
  searchOrderHistory,
  selectOrderHistory,
  selectOrderHistoryStatus,
} from "../features/orders/storage/orders.slice";
import { OrdersRequestCache } from "../features/orders/storage/types";
import { MAX_ORDERS_PAGE_SIZE } from "../features/orders/utils/consts";
import { AgreementsListConatiner } from "../features/sales-agreements/ActiveAgreements";
import NoSalesAgreement from "../features/sales-agreements/NoSalesAgreement";
import SalesAgreementListItem from "../features/sales-agreements/SalesAgreementListItem";
import {
  getSalesAgreements,
  selectActiveSalesAgreements,
  selectAgreementsRequestStatus,
  selectAllSalesAgreements,
} from "../features/sales-agreements/storage";
import { useAnyPermission } from "../hooks/useAnyPermission";
import { usePermission } from "../hooks/usePermission";
import { DashboardPromotionsAtomic } from "../services/atomic/DashboardPromotionsAtomic";
import { useAppDispatch } from "../store/rootStore";
import { TestIds } from "../testIds";
import hashObject from "../util/hash";

const DashboardContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: "16px",
  [theme.breakpoints.up("md")]: {
    gap: "32px",
  },
}));

const ContentWrapper = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: "16px",
});

const TitleWrapper = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  maxWidth: "100%",
  flex: 1,
  [theme.breakpoints.up("lg")]: {
    maxWidth: "688px",
  },

  h1: {
    [theme.breakpoints.down("md")]: {
      fontSize: "1.75rem",
    },
  },
}));

const Contents = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  // maxWidth: '100%',
  flex: 1,
  gap: "16px",
  [theme.breakpoints.up("lg")]: {
    // maxWidth: '1180px',
    gap: "32px",
  },
}));

export function DeprecatedHome() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const account = useSelector(selectCurrentFarmerAccount);

  const { hash } = React.useMemo(() => {
    const obj = {
      a: undefined,
      b: undefined,
      c: "",
      d: undefined,
      e: account?.accountNumber,
    };
    return {
      hash: hashObject(obj),
      obj: obj,
    };
  }, [account]);

  const orders = useSelector(selectOrderHistory(hash));
  const salesAgreements = useSelector(selectAllSalesAgreements);
  const activeAgreements = useSelector(selectActiveSalesAgreements);

  const ordersStatus: OrdersRequestCache | null = useSelector(selectOrderHistoryStatus(hash));
  const agreementsStatus = useSelector(selectAgreementsRequestStatus);

  const username = useSelector(selectName);

  const relationshipOwner = useSelector(selectContactInfo);

  const permissionsLoading = useAnyPermission([
    "Orders_OrderView",
    "SalesAgreements_AgreementView",
  ]).isLoading;
  const canViewSalesAgreements = usePermission("SalesAgreements_AgreementView").allowed;
  const canViewOrders = usePermission("Orders_OrderView").allowed;
  const canViewShop = usePermission("FarmlandsProShop_ShopView").allowed;

  React.useEffect(() => {
    if (account) {
      if (canViewOrders) {
        dispatch(
          searchOrderHistory({
            accountNumber: account.accountNumber,
            skip: 0,
            top: MAX_ORDERS_PAGE_SIZE,
            startDateTime: undefined,
            endDateTime: undefined,
            salesId: "",
            deliveryMode: undefined,
            summaryOnly: true,
          })
        );
      }
      if (canViewSalesAgreements) {
        dispatch(getSalesAgreements(account.accountNumber));
      }
    }
  }, [account?.accountNumber, canViewOrders, canViewSalesAgreements, dispatch]);

  const loading = React.useMemo(() => {
    const salesAgreementsToShow =
      canViewSalesAgreements && salesAgreements.length === 0 && agreementsStatus !== "fulfilled";
    const ordersToShow =
      canViewOrders &&
      orders.length === 0 &&
      ordersStatus?.status !== "fulfilled" &&
      ordersStatus?.status !== "rejected";
    if (salesAgreementsToShow || ordersToShow || permissionsLoading) {
      return true;
    }

    return false;
  }, [
    ordersStatus,
    agreementsStatus,
    salesAgreements,
    orders,
    canViewSalesAgreements,
    canViewOrders,
    permissionsLoading,
  ]);

  const renderOrders = React.useMemo(() => {
    if (!canViewOrders) {
      return null;
    }
    const list = orders.slice(0, 2);
    return (
      <ContentWrapper data-testid={TestIds.feat.home.ordersSection}>
        <TitleWithLink
          title="Recent orders"
          linkTitle="View all orders"
          linkTo={() => navigate("/orders")}
          testId={TestIds.feat.dashboard.list.viewAllOrders}
        />
        {list.length === 0 ? (
          <NoOrders
            bodyText={"You do not have any recent orders."}
            bodyPropsSx={{ marginBottom: 2 }}
          />
        ) : (
          list.map((x) => <OrderCard data={x} key={x.salesId} />)
        )}
      </ContentWrapper>
    );
  }, [canViewOrders, navigate, orders]);

  const renderActiveAgreements = React.useMemo(() => {
    if (!canViewSalesAgreements) {
      return null;
    }
    if (_.isEmpty(salesAgreements)) {
      return null;
    }
    const list = activeAgreements.slice(0, 2);
    const noActiveAgreements = (
      <NoSalesAgreement body={"You do not have any active Sales Agreements."} />
    );
    return (
      <ContentWrapper data-testid={TestIds.feat.home.agreementsSection}>
        <TitleWithLink
          title="Active sales agreements"
          linkTitle="View all"
          linkTo={() => navigate("/agreements")}
          testId={TestIds.feat.dashboard.list.viewAllAgreements}
        />
        <AgreementsListConatiner
          data-testid={TestIds.feat.agreement.tabView.activeAgreementsList}
          sx={{ gap: 2, margin: 0 }}
        >
          {list.length === 0
            ? noActiveAgreements
            : list.map((e) => (
                <SalesAgreementListItem
                  key={e.id}
                  {...e}
                  products={e.lines.map((p) => p.productDescription)}
                  percentageFulfilled={e.percentageFulfilled}
                />
              ))}
        </AgreementsListConatiner>
      </ContentWrapper>
    );
  }, [activeAgreements, salesAgreements, canViewSalesAgreements]);

  const renderContactCard = React.useMemo(() => {
    return (
      <ContactCard
        title={`Contact your Farmlands representative if you have any questions about your Farmlands account and FarmlandsPRO. `}
        sx={{ minWidth: "100%" }}
        {...relationshipOwner}
      />
    );
  }, [relationshipOwner]);

  const showDefaultContents = React.useMemo(() => {
    const noAgreementsForUser = !canViewSalesAgreements || _.isEmpty(salesAgreements);
    const noOrdersForUser = !canViewOrders || orders.length === 0;
    return noOrdersForUser && noAgreementsForUser;
  }, [orders, salesAgreements, canViewSalesAgreements, canViewOrders]);

  const renderHomePage = () => {
    return (
      <PageContainer data-testid={TestIds.pages.home}>
        {loading && (
          <Box display="flex" height="100%" width="100%" data-testid={TestIds.feat.home.loader}>
            <Loader />
          </Box>
        )}
        {!loading && (
          <DashboardContainer>
            <Section pb={1}>
              <TitleWrapper>
                <TitleBox display={`Kia Ora ${username}`} />
              </TitleWrapper>
            </Section>

            <Box display="flex" gap={2}>
              <Contents>
                {canViewShop && <SearchBar />}
                <QuickLinks />
                <DashboardPromotionsAtomic />
                {!showDefaultContents && (
                  <>
                    {renderOrders}
                    {renderActiveAgreements}
                  </>
                )}
                {renderContactCard}
              </Contents>
            </Box>
          </DashboardContainer>
        )}
      </PageContainer>
    );
  };

  return renderHomePage();
}
export default DeprecatedHome;
